import { AxiosError, AxiosResponse, ResponseType } from "axios";
import { StatusCode } from "../models/Enums";
import { ErrorModel } from "../models/ErrorModel";
import { instance } from "./instance";
import { loaderService, notificationService } from "./notificationService";


/**
 * Make a server call for POST type
 * @param data: Generic type of request data
 * @param url: An endpoint
 */
export const postCall = <T extends unknown>(url: string, data: T, config?: Record<string, any>) => {
    try {
        // Detect FormData and let axios set the correct Content-Type with boundary.
        // Use a 15-minute timeout for file uploads since large files (up to 100MB)
        // need time for upload + server-side virus scanning via managed instance.
        const requestConfig = data instanceof FormData
            ? { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 900000, skipGlobalLoader: true, ...config }
            : config;

        const response = instance
            .post(url, data, requestConfig)
            .then((response: AxiosResponse) => {
                loaderService.setLoading(false);
                return response.data;
            })
            .catch((error: AxiosError | ErrorModel) => {
                loaderService.clearLoading();
                // Note: 401 errors are already handled by axios interceptor in instance.tsx
                // which redirects to login automatically
                if (error instanceof ErrorModel) {
                    notificationService.sendMessage(error);
                    throw error;
                }
                notificationService.sendMessage(
                    new ErrorModel(error.message, StatusCode.InternalServerError)
                );
                throw error;
            });

        return response;
    } catch (error) {
        loaderService.clearLoading();
        throw error;
    }
};

/**
 * Make a server call for POST type for file
 * @param data: Generic type of request data
 * @param url: An endpoint
 */
export const postCallFile = <T extends unknown>(url: string, data: T) => {
    try {
        const response = instance
            .post(url, data,
                {
                    responseType: 'blob',
                })
            .then((response: AxiosResponse) => {
                loaderService.setLoading(false);
                return response.data;
            })
            .catch((error: AxiosError | ErrorModel) => {
                loaderService.clearLoading();
                // Note: 401 errors are already handled by axios interceptor in instance.tsx
                // which redirects to login automatically
                if (error instanceof ErrorModel) {
                    notificationService.sendMessage(error);
                    throw error;
                }
                notificationService.sendMessage(
                    new ErrorModel(error.message, StatusCode.InternalServerError)
                );
                throw error;
            });

        return response;
    } catch (error) {
        loaderService.clearLoading();
        throw error;
    }
};

/**
 * Make a server call for PATCH type
 * @param data: Generic type of request data
 * @param url: An endpoint
 */
export const patchCall = <T extends unknown>(url: string, data: T) => {
    try {
        const config = data instanceof FormData
            ? { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 900000, skipGlobalLoader: true }
            : undefined;

        const response = instance
            .patch(url, data, config)
            .then((response: AxiosResponse) => {
                loaderService.setLoading(false);
                return response.data;
            })
            .catch((error: AxiosError | ErrorModel) => {
                loaderService.clearLoading();
                // Note: 401 errors are already handled by axios interceptor in instance.tsx
                // which redirects to login automatically
                if (error instanceof ErrorModel) {
                    notificationService.sendMessage(error);
                    throw error;
                }
                notificationService.sendMessage(
                    new ErrorModel(error.message, StatusCode.InternalServerError)
                );
                throw error;
            });

        return response;
    } catch (error) {
        loaderService.clearLoading();
        throw error;
    }
};

/**
* Make a server call for HTTP Get
* @param url: An endpoint
* @param responseType: Response type of the response
* @param showNotificationOnNotFoundError: Show notification error message for StatusCode other that 404(NotFound)
*/
export const getCall = (url: string, responseType: ResponseType = "json", showNotificationOnNotFoundError: boolean = true) => {
    try {
        const response = instance
            .get(url, { responseType })
            .then((response: AxiosResponse) => {
                loaderService.setLoading(false);
                return response.data;
            })
            .catch((error: AxiosError | ErrorModel) => {
                loaderService.clearLoading();
                // Note: 401 errors are already handled by axios interceptor in instance.tsx
                // which redirects to login automatically
                if (error instanceof ErrorModel) {
                    if (error.statusCode === StatusCode.NotFound && !showNotificationOnNotFoundError) {
                        throw error;
                    }

                    notificationService.sendMessage(error);
                    throw error;
                }
                notificationService.sendMessage(
                    new ErrorModel(error.message, StatusCode.InternalServerError)
                );
                throw error;
            });

        return response;
    } catch (error) {
        loaderService.clearLoading();
        throw error;
    }
};

/**
 * Make a server call for PUT type
 * @param data: Request data
 * @param url: An endpoint
 */
export const putCall = <T extends unknown>(url: string, data: T) => {
    try {
        const config = data instanceof FormData
            ? { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 900000, skipGlobalLoader: true }
            : undefined;

        const response = instance
            .put(url, data, config)
            .then((response: AxiosResponse) => {
                loaderService.setLoading(false);
                return response.data;
            })
            .catch((error: AxiosError | ErrorModel) => {
                loaderService.clearLoading();
                // Note: 401 errors are already handled by axios interceptor in instance.tsx
                // which redirects to login automatically
                if (error instanceof ErrorModel) {
                    notificationService.sendMessage(error);
                    throw error;
                }
                notificationService.sendMessage(
                    new ErrorModel(error.message, StatusCode.InternalServerError)
                );
                throw error;
            });

        return response;
    } catch (error) {
        loaderService.clearLoading();
        throw error;
    }
};

/**
 * Make a server call for DELETE type
 * @param data: Request data
 * @param url: An endpoint
 */
export const deleteCall = <T extends unknown>(url: string, data: T) => {
    try {
        const response = instance
            .delete(url, { data })
            .then((response: AxiosResponse) => {
                loaderService.setLoading(false);
                return response.data;
            })
            .catch((error: AxiosError | ErrorModel) => {
                loaderService.clearLoading();
                // Note: 401 errors are already handled by axios interceptor in instance.tsx
                // which redirects to login automatically
                if (error instanceof ErrorModel) {
                    notificationService.sendMessage(error);
                    throw error;
                }
                notificationService.sendMessage(
                    new ErrorModel(error.message, StatusCode.InternalServerError)
                );
                throw error;
            });

        return response;
    } catch (error) {
        loaderService.clearLoading();
        throw error;
    }
};